import { Theme } from '@mui/material';
import { LabelAssignment } from '@provision/entity-types';
import { NON_LABEL_GROUP_ID } from 'src/constants/label';
import { CUSTOM_VIEW_GROUPS } from 'src/graphql/entities/custom-view/constants';
import {
  CustomViewAttribute,
  UIType,
} from 'src/graphql/entities/custom-view/custom-view.types';
import { UserGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';
import {
  DEACTIVATED_USER_COLOR,
  DEACTIVATED_USER_TEXT,
  hasDisabledText,
} from 'src/graphql/entities/platform-user';
import { pickPastelColor } from 'src/utils/strings';
import {
  ConversationAttribute,
  ConversationAttributeAccessors,
  ConversationAttributeType,
} from './constants';

export const conversationAttributeConfigList: CustomViewAttribute<ConversationAttributeType>[] =
  [
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.conversationUrgency,
      attributeLabel: 'Urgency',
      uiType: UIType.Custom,
      default: true,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.id,
      attributeLabel: 'Conversation ID',
      uiType: UIType.Number,
      default: true,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.conversationUuid,
      attributeLabel: 'Conversation UUID',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.conversationStatus,
      attributeLabel: 'Status',
      uiType: UIType.Custom,
      default: true,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.subject,
      attributeLabel: 'Subject',
      uiType: UIType.Text,
      default: true,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.type,
      attributeLabel: 'Type',
      uiType: UIType.Text,
      default: true,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.requesterFullName,
      attributeLabel: 'Requester',
      uiType: UIType.Text,
      default: true,
      getTextColor: (value: unknown): string => {
        return hasDisabledText(value as string) ? DEACTIVATED_USER_COLOR : '';
      },
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.fullSiteAddress,
      attributeLabel: 'Site Address',
      uiType: UIType.Text,
      default: true,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.primaryClientName,
      attributeLabel: 'Primary Client',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.serviceLevel,
      attributeLabel: 'Service Level',
      uiType: UIType.Custom,
      default: true,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.lastMessageReceivedAt,
      attributeLabel: 'Messaged At',
      uiType: UIType.SinceTime,
      default: true,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.unseenCount,
      attributeLabel: 'Unseen messages',
      uiType: UIType.BubbleNumber,
      default: true,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.requesterEmail,
      attributeLabel: 'Requester Email',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.requesterPhone,
      attributeLabel: 'Requester Phone',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.requesterType,
      formatValue: (value: string) => {
        const valuesMap = {
          platform_user: 'User',
          client: 'Client',
        };

        return valuesMap[value as keyof typeof valuesMap] ?? '';
      },
      attributeLabel: 'Requester Type',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.assigneeGroup,
      attributeLabel: 'Group',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.assigneeFullName,
      attributeLabel: 'Assignee',
      getTextColor: (value: unknown): string => {
        return hasDisabledText(value as string) ? DEACTIVATED_USER_COLOR : '';
      },
      getSubTitleText: (value: string): string => {
        return hasDisabledText(value) ? ` (${DEACTIVATED_USER_TEXT})` : '';
      },
      uiType: UIType.Avatar,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.snoozeUntil,
      dateFormat: 'MMM DD',
      includeTime: true,
      attributeLabel: 'Snoozed Until',
      uiType: UIType.Date,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Partner,
      attribute: ConversationAttribute.partnerName,
      attributeLabel: 'Partner Name',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Partner,
      attribute: ConversationAttribute.timeZone,
      attributeLabel: 'Timezone',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Partner,
      attribute: ConversationAttribute.partnerId,
      attributeLabel: 'Partner ID',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.requesterId,
      attributeLabel: 'Requester ID',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.createdAt,
      attributeLabel: 'Created At',
      uiType: UIType.Date,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.deletedAt,
      attributeLabel: 'Deleted At',
      uiType: UIType.Date,
      allowedRules: [UserGroupRules.AccessToRestoreDeletedConversations],
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.conversationStatusUpdatedAt,
      attributeLabel: 'Status Updated at',
      uiType: UIType.SinceTime,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Conversation,
      attribute: ConversationAttribute.label,
      attributeLabel: 'Labels',
      uiType: UIType.Chips,
      getBackgroundColor: (value: unknown, theme: Theme): string => {
        return pickPastelColor(
          (value as LabelAssignment).label.labelGroupId ?? NON_LABEL_GROUP_ID,
          theme.palette.mode,
        );
      },
      renderValue: (value: unknown) => (value as LabelAssignment).label.displayName,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.addressId,
      attributeLabel: 'Site ID',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.addressCity,
      attributeLabel: 'City',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.addressZip,
      attributeLabel: 'Zip/Postal Code',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.addressCppExpDate,
      attributeLabel: 'CPP Expiration',
      uiType: UIType.Date,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.membershipRegistered,
      attributeLabel: 'Membership Registered',
      uiType: UIType.Boolean,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.addressName,
      attributeLabel: 'Business Name',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.address,
      attributeLabel: 'Address',
      uiType: UIType.Text,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.clientTosAccepted,
      attributeLabel: 'TOS',
      uiType: UIType.Boolean,
    },
    {
      group: CUSTOM_VIEW_GROUPS.Site,
      attribute: ConversationAttribute.clientSegment,
      attributeLabel: 'Client Segment',
      uiType: UIType.Text,
    },
  ].map((i) => ({
    ...i,
    accessor: ConversationAttributeAccessors[i.attribute].display,
    sortingKey: ConversationAttributeAccessors[i.attribute].sort,
  }));

export const conversationAttributeConfigMap = new Map(
  conversationAttributeConfigList.map((i) => [i.attribute, i]),
);
